import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { settingsApi, maintenanceApi } from '@/services/api'
import { useAuth } from '@/hooks/useAuth'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import Alert from '@/components/ui/Alert'
import Loading from '@/components/ui/Loading'
import { Settings as SettingsIcon, DollarSign, Percent, Users, CreditCard, List, AlertTriangle, Bell } from 'lucide-react'

export default function Settings() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [formData, setFormData] = useState({
    withdrawal_rate: '',
    escrow_fee_percentage: '',
    max_barters_per_group: '',
    pagination_items_per_page: '',
    notification_dismissal_days: '',
    subscription_monthly_price: '',
    free_tier_active_barter_limit: '',
    free_tier_inactive_barter_limit: '',
    paid_tier_active_barter_limit: '',
    paid_tier_inactive_barter_limit: '',
  })

  const [maintenanceData, setMaintenanceData] = useState({
    is_enabled: false,
    maintenance_message: '',
    start_datetime: '',
    end_datetime: '',
  })

  const { data, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: () => settingsApi.get(),
  })

  const { data: maintenanceInfo } = useQuery({
    queryKey: ['maintenance'],
    queryFn: () => maintenanceApi.get(),
  })

  useEffect(() => {
    if (data?.data?.settings) {
      const settings = data.data.settings
      setFormData({
        withdrawal_rate: settings.withdrawal_rate || '',
        escrow_fee_percentage: settings.escrow_fee_percentage || '',
        max_barters_per_group: settings.max_barters_per_group || '',
        pagination_items_per_page: settings.pagination_items_per_page || '',
        notification_dismissal_days: settings.notification_dismissal_days || '7',
        subscription_monthly_price: settings.subscription_monthly_price || '',
        free_tier_active_barter_limit: settings.free_tier_active_barter_limit || '',
        free_tier_inactive_barter_limit: settings.free_tier_inactive_barter_limit || '',
        paid_tier_active_barter_limit: settings.paid_tier_active_barter_limit || '',
        paid_tier_inactive_barter_limit: settings.paid_tier_inactive_barter_limit || '',
      })
    }
  }, [data])

  useEffect(() => {
    if (maintenanceInfo?.data?.maintenance) {
      const maintenance = maintenanceInfo.data.maintenance
      setMaintenanceData({
        is_enabled: maintenance.is_enabled === 1,
        maintenance_message: maintenance.maintenance_message || '',
        start_datetime: maintenance.start_datetime ? maintenance.start_datetime.replace(' ', 'T').substring(0, 16) : '',
        end_datetime: maintenance.end_datetime ? maintenance.end_datetime.replace(' ', 'T').substring(0, 16) : '',
      })
    }
  }, [maintenanceInfo])

  const updateMutation = useMutation({
    mutationFn: (data: any) => settingsApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] })
      setSuccess('Settings updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update settings')
    },
  })

  const updateMaintenanceMutation = useMutation({
    mutationFn: (data: any) => maintenanceApi.update(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['maintenance'] })
      setSuccess('Maintenance mode updated successfully!')
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to update maintenance mode')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate
    const withdrawalRate = parseFloat(formData.withdrawal_rate)
    const escrowFee = parseFloat(formData.escrow_fee_percentage)
    const maxBarters = parseInt(formData.max_barters_per_group)
    const paginationItems = parseInt(formData.pagination_items_per_page)

    if (withdrawalRate < 0 || withdrawalRate > 1) {
      setError('Withdrawal rate must be between 0 and 1')
      return
    }

    if (escrowFee < 0 || escrowFee > 100) {
      setError('Escrow fee must be between 0 and 100')
      return
    }

    if (maxBarters < 2 || maxBarters > 10) {
      setError('Max barters per group must be between 2 and 10')
      return
    }

    if (paginationItems < 5 || paginationItems > 100) {
      setError('Pagination items per page must be between 5 and 100')
      return
    }

    const notificationDismissalDays = parseInt(formData.notification_dismissal_days)
    if (notificationDismissalDays < 1 || notificationDismissalDays > 365) {
      setError('Notification dismissal days must be between 1 and 365')
      return
    }

    // Validate barter limits
    const freeActiveLimit = parseInt(formData.free_tier_active_barter_limit)
    const freeInactiveLimit = parseInt(formData.free_tier_inactive_barter_limit)
    const paidActiveLimit = parseInt(formData.paid_tier_active_barter_limit)
    const paidInactiveLimit = parseInt(formData.paid_tier_inactive_barter_limit)

    if (freeActiveLimit < 1 || freeActiveLimit > 10) {
      setError('Free tier active barter limit must be between 1 and 10')
      return
    }

    if (freeInactiveLimit < 1 || freeInactiveLimit > 20) {
      setError('Free tier inactive barter limit must be between 1 and 20')
      return
    }

    if (paidActiveLimit < 1 || paidActiveLimit > 100) {
      setError('Paid tier active barter limit must be between 1 and 100')
      return
    }

    if (paidInactiveLimit < 1 || paidInactiveLimit > 100) {
      setError('Paid tier inactive barter limit must be between 1 and 100')
      return
    }

    updateMutation.mutate(formData)
  }

  const handleMaintenanceSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Validate datetime if enabled
    if (maintenanceData.is_enabled && maintenanceData.start_datetime && maintenanceData.end_datetime) {
      const start = new Date(maintenanceData.start_datetime)
      const end = new Date(maintenanceData.end_datetime)

      if (end <= start) {
        setError('End date/time must be after start date/time')
        return
      }
    }

    updateMaintenanceMutation.mutate(maintenanceData)
  }

  // Check if user is super admin
  if (user?.user_type !== 'super_admin') {
    return (
      <div className="max-w-4xl mx-auto">
        <Alert
          type="error"
          message="Access Denied: Only Super Admins can access system settings."
        />
      </div>
    )
  }

  if (isLoading) return <Loading />

  return (
    <div className="max-w-4xl mx-auto space-y-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-3">
        <SettingsIcon className="w-8 h-8 text-primary-600" />
        <h1 className="text-2xl sm:text-3xl font-bold">System Settings</h1>
      </div>

      {success && <Alert type="success" message={success} onClose={() => setSuccess('')} />}
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      {/* Maintenance Mode Settings */}
      <form onSubmit={handleMaintenanceSubmit}>
        <Card title="Maintenance Mode" className="border-yellow-200 bg-yellow-50">
          <div className="space-y-4">
            <div className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-yellow-300">
              <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-900">
                <p className="font-semibold mb-1">Important:</p>
                <p>When maintenance mode is enabled and within the specified time window, users will not be able to log in or register. Existing sessions will see a maintenance notice banner.</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="maintenance_enabled"
                checked={maintenanceData.is_enabled}
                onChange={(e) => setMaintenanceData({ ...maintenanceData, is_enabled: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="maintenance_enabled" className="label mb-0">
                Enable Maintenance Mode
              </label>
            </div>

            <div>
              <label className="label">Maintenance Message *</label>
              <textarea
                className="textarea"
                rows={3}
                value={maintenanceData.maintenance_message}
                onChange={(e) => setMaintenanceData({ ...maintenanceData, maintenance_message: e.target.value })}
                placeholder="We are currently performing scheduled maintenance. We will be back shortly!"
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                This message will be displayed to users during maintenance
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Start Date/Time</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={maintenanceData.start_datetime}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, start_datetime: e.target.value })}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave empty for immediate effect
                </p>
              </div>

              <div>
                <label className="label">End Date/Time</label>
                <input
                  type="datetime-local"
                  className="input"
                  value={maintenanceData.end_datetime}
                  onChange={(e) => setMaintenanceData({ ...maintenanceData, end_datetime: e.target.value })}
                />
                <p className="text-sm text-gray-500 mt-1">
                  Leave empty for indefinite maintenance
                </p>
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t border-yellow-200">
              <Button type="submit" isLoading={updateMaintenanceMutation.isPending}>
                <AlertTriangle className="w-4 h-4 mr-2" />
                Update Maintenance Mode
              </Button>
            </div>
          </div>
        </Card>
      </form>

      <form onSubmit={handleSubmit}>
        {/* Financial Settings */}
        <Card title="Financial Settings">
          <div className="space-y-6">
            <div>
              <label className="label">
                <DollarSign className="w-4 h-4 inline mr-2" />
                Withdrawal Rate *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="1"
                className="input"
                value={formData.withdrawal_rate}
                onChange={(e) => setFormData({ ...formData, withdrawal_rate: e.target.value })}
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Amount users receive per coin when withdrawing (e.g., 0.95 = $0.95 per coin)
              </p>
            </div>

            <div>
              <label className="label">
                <Percent className="w-4 h-4 inline mr-2" />
                Escrow Fee Percentage *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                className="input"
                value={formData.escrow_fee_percentage}
                onChange={(e) =>
                  setFormData({ ...formData, escrow_fee_percentage: e.target.value })
                }
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Percentage of barter value held in escrow (e.g., 10 = 10%)
              </p>
            </div>
          </div>
        </Card>

        {/* Barter Settings */}
        <Card title="Barter Settings" className="mt-6">
          <div className="space-y-6">
            <div>
              <label className="label">
                <Users className="w-4 h-4 inline mr-2" />
                Max Barters Per Group *
              </label>
              <input
                type="number"
                min="2"
                max="10"
                className="input"
                value={formData.max_barters_per_group}
                onChange={(e) =>
                  setFormData({ ...formData, max_barters_per_group: e.target.value })
                }
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Maximum number of barters allowed in a single group (2-10)
              </p>
            </div>

            <div>
              <label className="label">
                <List className="w-4 h-4 inline mr-2" />
                Pagination Items Per Page *
              </label>
              <input
                type="number"
                min="5"
                max="100"
                className="input"
                value={formData.pagination_items_per_page}
                onChange={(e) =>
                  setFormData({ ...formData, pagination_items_per_page: e.target.value })
                }
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Default number of items displayed per page in tables (5-100)
              </p>
            </div>

            <div>
              <label className="label">
                <Bell className="w-4 h-4 inline mr-2" />
                Notification Dismissal Duration (Days) *
              </label>
              <input
                type="number"
                min="1"
                max="365"
                className="input"
                value={formData.notification_dismissal_days}
                onChange={(e) =>
                  setFormData({ ...formData, notification_dismissal_days: e.target.value })
                }
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Number of days before dismissed page notifications reappear (1-365). Notifications also reappear if updated by an admin.
              </p>
            </div>
          </div>
        </Card>

        {/* Stripe Configuration Notice */}
        <Card title="Stripe Integration" className="mt-6 bg-yellow-50 border-yellow-200">
          <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
            <CreditCard className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-yellow-900 flex-1">
              <p className="font-semibold mb-2">Stripe keys are now configured via environment variables</p>
              <p className="mb-3">
                For security reasons, Stripe API keys are no longer stored in the database.
                Configure them in your <code className="bg-yellow-100 px-2 py-1 rounded break-all">.env</code> file:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-2 font-mono text-xs bg-white p-3 sm:p-4 rounded border border-yellow-300 overflow-x-auto">
                <li className="break-all">STRIPE_TEST_MODE=true</li>
                <li className="break-all">STRIPE_TEST_PUBLISHABLE_KEY=pk_test_...</li>
                <li className="break-all">STRIPE_TEST_SECRET_KEY=sk_test_...</li>
                <li className="break-all">STRIPE_LIVE_PUBLISHABLE_KEY=pk_live_...</li>
                <li className="break-all">STRIPE_LIVE_SECRET_KEY=sk_live_...</li>
                <li className="break-all">STRIPE_WEBHOOK_SECRET=whsec_...</li>
              </ul>
              <p className="mt-3 text-xs">
                After updating the .env file, restart the backend container for changes to take effect.
              </p>
            </div>
          </div>
        </Card>

        {/* Subscription Settings */}
        <Card title="Subscription Settings" className="mt-6">
          <div className="space-y-6">
            <div>
              <label className="label">
                <DollarSign className="w-4 h-4 inline mr-2" />
                Monthly Subscription Price *
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                className="input"
                value={formData.subscription_monthly_price}
                onChange={(e) =>
                  setFormData({ ...formData, subscription_monthly_price: e.target.value })
                }
                required
              />
              <p className="text-sm text-gray-500 mt-1">
                Monthly subscription fee for professional users (in USD)
              </p>
            </div>
          </div>
        </Card>

        {/* User Tier Limits */}
        <Card title="User Tier Limits" className="mt-6">
          <div className="space-y-6">
            <div className="pb-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Free Tier Limits</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="label">
                    <List className="w-4 h-4 inline mr-2" />
                    Active Barter Limit *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    className="input"
                    value={formData.free_tier_active_barter_limit}
                    onChange={(e) =>
                      setFormData({ ...formData, free_tier_active_barter_limit: e.target.value })
                    }
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Max active barters for free users (1-10)
                  </p>
                </div>

                <div>
                  <label className="label">
                    <List className="w-4 h-4 inline mr-2" />
                    Inactive Barter Limit *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    className="input"
                    value={formData.free_tier_inactive_barter_limit}
                    onChange={(e) =>
                      setFormData({ ...formData, free_tier_inactive_barter_limit: e.target.value })
                    }
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Max inactive barters for free users (1-20)
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Paid Tier Limits</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <label className="label">
                    <List className="w-4 h-4 inline mr-2" />
                    Active Barter Limit *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="input"
                    value={formData.paid_tier_active_barter_limit}
                    onChange={(e) =>
                      setFormData({ ...formData, paid_tier_active_barter_limit: e.target.value })
                    }
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Max active barters for paid users (1-100)
                  </p>
                </div>

                <div>
                  <label className="label">
                    <List className="w-4 h-4 inline mr-2" />
                    Inactive Barter Limit *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    className="input"
                    value={formData.paid_tier_inactive_barter_limit}
                    onChange={(e) =>
                      setFormData({ ...formData, paid_tier_inactive_barter_limit: e.target.value })
                    }
                    required
                  />
                  <p className="text-sm text-gray-500 mt-1">
                    Max inactive barters for paid users (1-100)
                  </p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Save Button */}
        <div className="mt-6 flex justify-center sm:justify-end">
          <Button type="submit" size="lg" isLoading={updateMutation.isPending} className="w-full sm:w-auto">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Save Settings
          </Button>
        </div>
      </form>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <div className="flex flex-col sm:flex-row sm:items-start space-y-3 sm:space-y-0 sm:space-x-3">
          <SettingsIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900 flex-1">
            <p className="font-semibold mb-1">Important:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>These settings affect the entire platform</li>
              <li>Changes take effect immediately</li>
              <li>API keys and secrets are configured via environment variables for security</li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Webhook Setup Instructions */}
      <Card title="Stripe Webhook Setup" className="bg-purple-50 border-purple-200">
        <div className="space-y-4 text-sm">
          <p className="font-semibold text-purple-900">
            To enable automatic subscription management, configure the following webhook in your Stripe Dashboard:
          </p>

          <div className="bg-white p-3 sm:p-4 rounded-lg border border-purple-200">
            <p className="font-semibold mb-2">Webhook Endpoint URL:</p>
            <code className="bg-gray-100 px-2 sm:px-3 py-2 rounded block text-xs break-all">
              {window.location.origin}/api/webhooks/stripe
            </code>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg border border-purple-200">
            <p className="font-semibold mb-2">Events to Subscribe:</p>
            <ul className="list-disc list-inside space-y-1 text-xs ml-2">
              <li className="break-all"><code>customer.subscription.created</code></li>
              <li className="break-all"><code>customer.subscription.updated</code></li>
              <li className="break-all"><code>customer.subscription.deleted</code></li>
              <li className="break-all"><code>invoice.payment_succeeded</code></li>
              <li className="break-all"><code>invoice.payment_failed</code></li>
              <li className="break-all"><code>checkout.session.completed</code></li>
            </ul>
          </div>

          <div className="bg-white p-3 sm:p-4 rounded-lg border border-purple-200">
            <p className="font-semibold mb-2">Setup Steps:</p>
            <ol className="list-decimal list-inside space-y-1 text-xs ml-2">
              <li>Go to Stripe Dashboard → Developers → Webhooks</li>
              <li>Click "Add endpoint"</li>
              <li>Enter the webhook URL above</li>
              <li>Select the events listed above</li>
              <li>Click "Add endpoint"</li>
              <li className="break-all">Copy the "Signing secret" (starts with whsec_)</li>
              <li className="break-all">Add it to your .env file as STRIPE_WEBHOOK_SECRET</li>
              <li>Restart the backend container</li>
            </ol>
          </div>

          <Alert type="info" message="The webhook is essential for managing subscription lifecycle events like renewals, cancellations, and payment failures." />
        </div>
      </Card>
    </div>
  )
}
